import { getValueFormatter } from '../utils/ag-grid/valueFormatters'
import {
  createAvgValueObject,
  createMarkUpValueObject,
} from '../utils/AggregationUtil'
import { getCellStyle } from '../views/Internal/FinancialRollup/color-coding-cell-style'
import moment from 'moment'

const ReportingConstants = {
  QUARTERS: [
    { name: 'Q1', id: 0, startMonth: 1, endMonth: 3 },
    { name: 'Q2', id: 1, startMonth: 4, endMonth: 6 },
    { name: 'Q3', id: 2, startMonth: 7, endMonth: 9 },
    { name: 'Q4', id: 3, startMonth: 10, endMonth: 12 },
  ],

  CYCLES: [
    { name: '1', id: 1 },
    { name: '2', id: 2 },
    { name: '3', id: 3 },
    { name: '4', id: 4 },
    { name: '5', id: 5 },
    { name: '6', id: 6 },
  ],

  YEAR_OPTIONS: [
    { name: (moment().year() - 3).toString(), id: moment().year() - 3 },
    { name: (moment().year() - 2).toString(), id: moment().year() - 2 },
    { name: (moment().year() - 1).toString(), id: moment().year() - 1 },
    { name: moment().year().toString(), id: moment().year() },
    { name: (moment().year() + 1).toString(), id: moment().year() + 1 },
  ],

  CYCLE_NUMBER_FILTER: {
    dimension: 'design_cycle_number',
    display_name: 'Development Cycle',
    obj_type: 'column',
    type: 'in',
    ref_id: '8wg7ftq6qu3',
    pattern: ['3'],
  },

  CYCLE_YEAR_FILTER: {
    dimension: 'design_cycle_year',
    display_name: 'Development Cycle Year',
    obj_type: 'column',
    type: 'in',
    ref_id: 'ec5ybkke2t',
    pattern: ['2021'],
  },

  /**
   * Top level reporting view
   */
  CHOOSE_PARAMETERS_PAGE: 'Choose Parameters',
  REPORT_PAGE: 'Report Page',
  NONE_TIMEFRAME_TYPE: 'none',
  EMPTY_STRING: '',

  /**
   * ReportBuilder
   */
  TIMEFRAME_EXPANDED: 'timeframe',
  PARAMETERS_EXPANDED: 'parameters',
  PERSONNEL_EXPANDED: 'personnel',
  METRICS_EXPANDED: 'metrics',
  NONE_EXPANDED: 'none',
  DATE_FORMAT: 'yyyy-MM-dd',
  DIGITS_ONLY_REGEX: /^\d+$/,

  /**
   * TimeframeSelector
   */
  DATE_RANGE_SELECTOR: 'between',
  QUARTERLY_SELECTOR: 'quarter',
  DEVELOPMENT_CYCLE_YEAR_FILTER: 'design_cycle_year',
  DEVELOPMENT_CYCLE_FILTER: 'design_cycle_number',
  DEVELOPMENT_CYCLE: 'development cycle',
  TIMEFRAME_PANEL_TITLE: 'Timeframe*',
  CHOOSE_MORE_PARAMETERS_BUTTON_TEXT: 'CHOOSE MORE PARAMETERS',
  DATE_RANGE_LABEL: 'Date Range',
  FISCAL_TIMEFRAME_LABEL: 'Fiscal Quarter',
  DEVELOPMENT_CYCLE_LABEL: 'Development Cycle',
  DEVELOPMENT_CYCLE_YEAR_LABEL: 'Development Cycle Year',
  TIMEFRAME_PANEL_BODY: 'Enter a timeframe to generate your report.',
  YEAR_LABEL: 'Year',
  QUARTER_LABEL: 'Fiscal Quarter',
  START_DATE: 'Start Date',
  END_DATE: 'End Date',
  TIMEFRAME_EXPANSION_PANEL_TEST_ID: 'timeframe_panel',
  TIMEFRAME_START_DATE_PICKER_TEST_ID: 'start_date_picker',
  TIMEFRAME_END_DATE_PICKER_TEST_ID: 'end_date_picker',
  TIMEFRAME_QUARTER_SELECT_TEST_ID: 'quarter_select',
  TIMEFRAME_YEAR_INPUT_TEST_ID: 'year_select',

  /**
   * ParametersSelector
   */
  STRING_FILTER_TYPES: ['in', 'not in'],
  PERSONNEL_FILTER_TYPES: [
    'event_create_user_email_id',
    'event_sourcing_manager_email_id',
    'event_sourcing_manager_leader_email_id',
    'event_sourcing_manager_leader_leader_email_id',
  ],
  GREENFIELD_MASTER_CARD_ID: '341785',
  MORE_PARAMETERS_TITLE: 'More Parameters',
  ADD_BUTTON_TEXT: 'ADD',
  PARAMETERS_EXPANSION_PANEL_TEST_ID: 'parameters_panel',
  FINANCIAL_METRICS_TITLE: 'Financial Metrics',

  /**
   * ParametersChipCard
   */
  SELECTED_FILTERS_TITLE: 'Selected Filters',
  EDIT_PARAMETERS_BUTTON_TEXT: 'EDIT PARAMETERS',
  CLEAR_BUTTON_TEXT: 'CLEAR',
  BUILD_REPORT_BUTTON_TEXT: 'BUILD REPORT',

  /**
   * GreenfieldGrid
   */
  TOO_MANY_ROWS_MESSAGE:
    'Rows exceeded 30k. Please narrow your timeframe or add additional filters to reduce your results.',
  AN_ERROR_OCCURRED_MESSAGE:
    'An error occurred while retrieving data. Please try again later. If the problem persists, contact us.',
  IN_FILTER: 'in',
  TABLE_ID: 'operationalReporting',
  USER_ID_COLUMN: 'event_create_user_id',
  SOURCING_MANAGER_COLUMN_HEADER: 'Sourcing Manager Email',
  WEIGHTED_AVG_COLUMNS: [
    {
      enableValue: true,
      colId: 'avg_first_cost',
      headerName: 'Average First Cost',
      valueGetter: params => {
        if (!params.node.group) {
          const totalFirstCost =
            params.data[params.context.fieldToRefIdMap.first_cost_a] ?? 0
          const units =
            (params.data[
              params.context.fieldToRefIdMap.estimated_total_receipt_units_q
            ]
              ? params.data[
                  params.context.fieldToRefIdMap.estimated_total_receipt_units_q
                ]
              : params.data[
                  params.context.fieldToRefIdMap
                    .vendor_estimated_total_receipt_units_q
                ]) ?? 0
          return createAvgValueObject(totalFirstCost, units)
        }
      },
      allowedAggFuncs: ['wtd_avg'],
      valueFormatter: getValueFormatter(null, '$0,0.00'),
      filterParams: {
        valueFormatter: getValueFormatter(null, '$0,0.00'),
      },
      multiValueFilterEligible: true,
      numericValueFilter: true,
      filter: true,
      sortable: true,
      menuTabs: ['filterMenuTab', 'generalMenuTab'],
      cellStyle: getCellStyle,
    },
    {
      enableValue: true,
      colId: 'avg_net_cost',
      headerName: 'Average Net Cost',
      valueGetter: params => {
        if (!params.node.group) {
          const totalNetCost =
            params.data[params.context.fieldToRefIdMap.net_cost_a] ?? 0
          const units =
            (params.data[
              params.context.fieldToRefIdMap.estimated_total_receipt_units_q
            ]
              ? params.data[
                  params.context.fieldToRefIdMap.estimated_total_receipt_units_q
                ]
              : params.data[
                  params.context.fieldToRefIdMap
                    .vendor_estimated_total_receipt_units_q
                ]) ?? 0
          return createAvgValueObject(totalNetCost, units)
        }
      },
      allowedAggFuncs: ['wtd_avg'],
      valueFormatter: getValueFormatter(null, '$0,0.00'),
      filterParams: {
        valueFormatter: getValueFormatter(null, '$0,0.00'),
      },
      multiValueFilterEligible: true,
      numericValueFilter: true,
      filter: true,
      sortable: true,
      menuTabs: ['filterMenuTab', 'generalMenuTab'],
      cellStyle: getCellStyle,
    },
    {
      enableValue: true,
      colId: 'imu',
      headerName: 'IMU',
      valueGetter: params => {
        if (!params.node.group) {
          const totalNetCost =
            params.data[params.context.fieldToRefIdMap.first_cost_a] ?? 0
          const totalRetail =
            params.data[params.context.fieldToRefIdMap.total_retail_a] ?? 0

          return createMarkUpValueObject(totalNetCost, totalRetail)
        }
      },
      allowedAggFuncs: ['avg_mark_up'],
      valueFormatter: getValueFormatter(null, '0.00%'),
      filterParams: {
        valueFormatter: getValueFormatter(null, '0.00%'),
      },
      multiValueFilterEligible: true,
      numericValueFilter: true,
      filter: true,
      sortable: true,
      menuTabs: ['filterMenuTab', 'generalMenuTab'],
      cellStyle: getCellStyle,
    },
    {
      enableValue: true,
      colId: 'pmu',
      headerName: 'Base PMU',
      valueGetter: params => {
        if (!params.node.group) {
          const totalNetCost =
            params.data[params.context.fieldToRefIdMap.net_cost_a] ?? 0
          const totalRetail =
            params.data[params.context.fieldToRefIdMap.total_retail_a] ?? 0

          return createMarkUpValueObject(totalNetCost, totalRetail)
        }
      },
      allowedAggFuncs: ['avg_mark_up'],
      valueFormatter: getValueFormatter(null, '0.00%'),
      filterParams: {
        valueFormatter: getValueFormatter(null, '0.00%'),
      },
      multiValueFilterEligible: true,
      numericValueFilter: true,
      filter: true,
      sortable: true,
      menuTabs: ['filterMenuTab', 'generalMenuTab'],
      cellStyle: getCellStyle,
    },
    {
      enableValue: true,
      colId: 'net_pmu',
      headerName: 'Net PMU',
      valueGetter: params => {
        if (!params.node.group) {
          const totalUnits =
            (params.data[
              params.context.fieldToRefIdMap.estimated_total_receipt_units_q
            ]
              ? params.data[
                  params.context.fieldToRefIdMap.estimated_total_receipt_units_q
                ]
              : params.data[
                  params.context.fieldToRefIdMap
                    .vendor_estimated_total_receipt_units_q
                ]) ?? 0
          const totalNetCost =
            (params.data[params.context.fieldToRefIdMap.net_cost_a] ?? 0) -
            totalUnits *
              (params.data[
                params.context.fieldToRefIdMap.vendor_income_per_unit_a
              ] ?? 0)
          const totalRetail =
            params.data[params.context.fieldToRefIdMap.total_retail_a] ?? 0

          return createMarkUpValueObject(totalNetCost, totalRetail)
        }
      },
      allowedAggFuncs: ['avg_mark_up'],
      valueFormatter: getValueFormatter(null, '0.00%'),
      filterParams: {
        valueFormatter: getValueFormatter(null, '0.00%'),
      },
      multiValueFilterEligible: true,
      numericValueFilter: true,
      filter: true,
      sortable: true,
      menuTabs: ['filterMenuTab', 'generalMenuTab'],
      cellStyle: getCellStyle,
    },
    {
      enableValue: true,
      colId: 'avgRetail',
      headerName: 'Average Retail',
      valueGetter: params => {
        if (!params.node.group) {
          const totalRetail =
            params.data[params.context.fieldToRefIdMap.total_retail_a] ?? 0
          const units =
            (params.data[
              params.context.fieldToRefIdMap.estimated_total_receipt_units_q
            ]
              ? params.data[
                  params.context.fieldToRefIdMap.estimated_total_receipt_units_q
                ]
              : params.data[
                  params.context.fieldToRefIdMap
                    .vendor_estimated_total_receipt_units_q
                ]) ?? 0
          return createAvgValueObject(totalRetail, units)
        }
      },
      allowedAggFuncs: ['wtd_avg'],
      valueFormatter: getValueFormatter(null, '$0,0.00'),
      filterParams: {
        valueFormatter: getValueFormatter(null, '$0,0.00'),
      },
      multiValueFilterEligible: true,
      numericValueFilter: true,
      filter: true,
      sortable: true,
      menuTabs: ['filterMenuTab', 'generalMenuTab'],
      cellStyle: getCellStyle,
    },
  ],
}

export default ReportingConstants
